/**
 * @fileoverview Compact progress stepper for the content workflow.
 *               Shows numbered steps as pills with labels. Current step
 *               is highlighted, completed steps show a checkmark.
 *               Adapts to workflow type: single post hides stream-only steps.
 *               PATH: src/components/contenthub/WorkflowStepper.js
 */

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { FiCheck } from 'react-icons/fi';
import { WORKFLOW_STEP_LIST } from '../shared/constants/workflowCopy';

function WorkflowStepper({ currentStepKey, workflowType }) {
    // Filter steps based on workflow type — single posts skip stream-only steps
    const visibleSteps = useMemo(() => {
        const filtered = workflowType === 'single'
            ? WORKFLOW_STEP_LIST.filter(s => !s.streamOnly)
            : WORKFLOW_STEP_LIST;

        // Re-number sequentially after filtering
        return filtered.map((step, idx) => ({ ...step, displayNumber: idx + 1 }));
    }, [workflowType]);

    // Find the index of the current step in the visible list
    const currentIndex = visibleSteps.findIndex(s => s.key === currentStepKey);

    return (
        <div className="w-full mb-4">
            {/* Desktop: horizontal stepper */}
            <div className="hidden md:flex items-center justify-center gap-1">
                {visibleSteps.map((step, idx) => {
                    const isCompleted = idx < currentIndex;
                    const isCurrent = idx === currentIndex;
                    const isFuture = idx > currentIndex;

                    return (
                        <React.Fragment key={step.key}>
                            {/* Step pill */}
                            <div className="flex items-center gap-2">
                                <div className={`
                                    flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold transition-all duration-300
                                    ${isCompleted ? 'bg-pastel-green text-warm-dark' : ''}
                                    ${isCurrent ? 'bg-slate-blue text-white shadow-lg shadow-slate-blue/30 ring-2 ring-slate-blue/30' : ''}
                                    ${isFuture ? 'bg-white/5 text-cream/30 border border-white/10' : ''}
                                `}>
                                    {isCompleted ? <FiCheck className="w-3.5 h-3.5" /> : step.displayNumber}
                                </div>
                                <span className={`
                                    text-xs font-medium whitespace-nowrap transition-colors duration-300
                                    ${isCompleted ? 'text-pastel-green' : ''}
                                    ${isCurrent ? 'text-cream font-bold' : ''}
                                    ${isFuture ? 'text-cream/25' : ''}
                                `}>
                                    {step.label}
                                </span>
                            </div>

                            {/* Connector line (not after last step) */}
                            {idx < visibleSteps.length - 1 && (
                                <div className={`
                                    flex-shrink-0 h-px w-6 transition-colors duration-300
                                    ${idx < currentIndex ? 'bg-pastel-green/50' : 'bg-white/10'}
                                `} />
                            )}
                        </React.Fragment>
                    );
                })}
            </div>

            {/* Mobile: compact bar with current step label */}
            <div className="md:hidden">
                {/* Progress bar */}
                <div className="flex gap-1 mb-2">
                    {visibleSteps.map((step, idx) => (
                        <div
                            key={step.key}
                            className={`
                                h-1 rounded-full flex-1 transition-all duration-300
                                ${idx < currentIndex ? 'bg-pastel-green' : ''}
                                ${idx === currentIndex ? 'bg-slate-blue' : ''}
                                ${idx > currentIndex ? 'bg-white/10' : ''}
                            `}
                        />
                    ))}
                </div>
                {/* Current step label */}
                <div className="flex justify-between items-center">
                    <span className="text-xs text-cream/40 font-medium">
                        Step {currentIndex + 1} of {visibleSteps.length}
                    </span>
                    <span className="text-xs text-cream font-bold">
                        {visibleSteps[currentIndex]?.label}
                    </span>
                </div>
            </div>
        </div>
    );
}

WorkflowStepper.propTypes = {
    currentStepKey: PropTypes.string.isRequired,
    workflowType: PropTypes.oneOf(['single', 'stream']).isRequired,
};

export default WorkflowStepper;
